import _ from 'lodash'
import { globalReduxStore } from '../../src/modules/initStore'
import { setReportEvent } from '../../src/redux/actions/activity'
import { isUpdated } from '../../src/utils/sync'
import { Cust_hasSingleLocation } from '../Cust/Cust.serverPrefs'
import { IDB_reportsReadAll, IDB_reportsWrite } from '../IDB/IDB.table.report'
import { Rpts_openReportFetch, Rpts_openReportWrite } from '../Rpts/Rpts.OpenReport'
import type { Serv_HttpRequestOptions } from '../Serv/Serv.httpRequest'
import { NReports } from '../Rpts/Rpts.types'
import { isOnline, Serv_send } from '../Serv/Serv.socketClient'
import type { Type_ICalendarEvent } from '../Type/Type.hive'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import { Logg_createLogger } from '../Logg/Logg.Logger'
import {
    IDB_calendarEventsDelete,
    IDB_calendarEventsReadAll,
    IDB_calendarEventsWrite,
} from '../IDB/IDB.table.calendarEvent'

export async function Cale_sync() {
    const logger = Logg_createLogger(`Cale_sync`)
    try {
        // Don't try to sync unless there's still a socket open to the server
        if (!(await isOnline())) return

        await uploadEventChanges(logger)

        if (!Cust_hasSingleLocation()) {
            await downloadCalendarEvents()
        }
    } catch (error) {
        logger.error(error)
    }
}

async function uploadEventChanges(logger: ClientLogger) {
    const events = await IDB_calendarEventsReadAll()

    for (const event of events) {
        // Skip all items without meta information
        if (event.meta === undefined) {
            continue
        }

        // Ideally we'd just have the 'new' check but we had a bug where some events were switched to
        // 'updated' before they could be uploaded so we use the onlineId check instead which is
        // more reliable anyway.
        if (event.meta.new === true || event.onlineId === undefined) {
            await syncNewEvent(logger, event)
        } else if (event.meta.updated === true) {
            await syncUpdatedEvent(event)
        } else if (event.meta.delete === true) {
            await syncDeletedEvent(event)
        }
    }
}

async function downloadCalendarEvents() {
    // Download the server events
    const serverEvents = (await Serv_send('v2.calendar.get', {})).events

    // Create a map of server online IDs for ease of access and lookup
    const serverOnlineIds: { [index: number]: any } = {}
    for (const event of serverEvents) {
        serverOnlineIds[event.id] = event
    }

    // Read the local events and create an array of all the online IDs there are locally
    const localEvents = await IDB_calendarEventsReadAll()
    const localOnlineIds = _.compact(localEvents.map((event) => event.onlineId))

    // Create an array of all the new server events (those for which we don't have an online ID)
    // And add to those objects the onlineId and transform the event author into the format expected by the app.
    const newEvents = serverEvents
        .filter((event) => !localOnlineIds.includes(event.id))
        .map((event) => ({ ...event, onlineId: event.id, author: event.author.id }) as Type_ICalendarEvent)

    // For those local events which do exist on the server, merge the server's values with the local values.
    // The server's values take precedence
    const syncedEvents = localEvents
        .filter((event) => event.onlineId && serverOnlineIds[event.onlineId] && !isUpdated(event))
        .map((event) => ({ ...event, ...serverOnlineIds[event.onlineId], id: event.id }))

    // For those local events which do exist on the server or have changes not yet uploaded, keep them as-is
    const unsyncedEvents = localEvents.filter(
        (event) =>
            event.onlineId === undefined || (event.onlineId && serverOnlineIds[event.onlineId] && isUpdated(event))
    )

    // All local events that still exist on the server + all new server events
    const eventsToSave = [].concat(newEvents, syncedEvents, unsyncedEvents)

    // Delete all the events in the database corresponding to the work just done
    const localIds = localEvents.map((event) => event.id)
    await IDB_calendarEventsDelete(localIds)

    // Add the changed events back into the database
    await IDB_calendarEventsWrite(eventsToSave)
}

export async function Cale_uploadNewEvent(event: Type_ICalendarEvent, sendOptions?: Serv_HttpRequestOptions) {
    const sendData = _.pick(
        event,
        'data',
        'startDate',
        'endDate',
        'recurring',
        'location',
        'campaign',
        'template',
        'createdDate'
    )

    const response = await Serv_send('calendar.add', sendData, sendOptions)
    const uploadedEvent: Type_ICalendarEvent = { ...event, onlineId: response.id, meta: {} }
    return uploadedEvent
}

async function syncNewEvent(logger: ClientLogger, event: Type_ICalendarEvent) {
    try {
        const uploadedEvent = await Cale_uploadNewEvent(event)

        // Read all the current reports fresh after the upload
        const [openReport, reports] = await Promise.all([Rpts_openReportFetch(), IDB_reportsReadAll()])
        const unuploadedReports = reports.filter(NReports.isUnuploadedReport)

        const unuploadedReport = unuploadedReports.find((r) => r.event && r.event.id === uploadedEvent.id)
        const isOpenReport = openReport != null && openReport.calendarEvent.id === event.id

        if (unuploadedReport) {
            await IDB_reportsWrite(logger, [{ ...unuploadedReport, event: uploadedEvent }])
        }
        if (isOpenReport) {
            await Rpts_openReportWrite({
                ...openReport,
                calendarEvent: uploadedEvent,
            })
            globalReduxStore.dispatch(setReportEvent(event))
        }
        // Update the local event, preserving the localId but without anything in meta
        await IDB_calendarEventsWrite([uploadedEvent])
    } catch (error) {
        logger.error(error)
    }
}

export async function Cale_uploadUpdatedEvent(event: Type_ICalendarEvent, sendOptions?: Serv_HttpRequestOptions) {
    // Create object to send to the server (we don't want the 'meta' data in there)
    const obj = {
        id: event.onlineId,
        data: event.data,
        startDate: event.startDate,
        endDate: event.endDate,
        recurring: event.recurring,
        location: event.location,
        campaign: event.campaign,
        template: event.template,
    }

    // Update the server with new object
    return Serv_send('calendar.update', obj, sendOptions)
}

async function syncUpdatedEvent(event: Type_ICalendarEvent) {
    // Update the server with new object
    try {
        await Cale_uploadUpdatedEvent(event)
    } catch (error) {
        return
    }

    // Update the local db to remove meta
    const updatedEvent = { ...event, meta: {} }

    try {
        await IDB_calendarEventsWrite([updatedEvent])
    } catch (error) {
        return
    }
}

export function Cale_uploadDeletedEvent(event: Type_ICalendarEvent, sendOptions?: Serv_HttpRequestOptions) {
    return Serv_send('calendar.delete', { id: event.onlineId }, sendOptions)
}

async function syncDeletedEvent(event: Type_ICalendarEvent) {
    // Delete smart data on the server
    if (event.onlineId) {
        try {
            await Cale_uploadDeletedEvent(event)
        } catch (error) {
            return
        }
    }

    // Delete smart data from local db

    try {
        await IDB_calendarEventsDelete([event.id])
    } catch (error) {
        return
    }
}
